import { Layout, Row, Segmented, Typography, theme } from 'antd';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AgentFiltersMandatoryQueryKeys,
    AgentFiltersQueryKeyMap
} from '../../../containers/Pages/ReportsPage/AgentReport/AgentReportFiltersContainer';
import {
    AgentStatusFiltersMandatoryQueryKeys,
    AgentStatusFiltersQueryKeyMap
} from '../../../containers/Pages/ReportsPage/AgentStatusReport/AgentStatusReportFiltersContainer';
import {
    AgentStatusSnapshotFiltersMandatoryQueryKeys,
    AgentStatusSnapshotFiltersQueryKeyMap
} from '../../../containers/Pages/ReportsPage/AgentStatusSnapshotReport/AgentStatusSnapshotReportFiltersContainer';
import {
    CallFiltersMandatoryQueryKeys,
    CallFiltersQueryKeyMap
} from '../../../containers/Pages/ReportsPage/CallReport/CallReportFiltersContainer';
import {
    ChatFiltersMandatoryQueryKeys,
    ChatFiltersQueryKeyMap
} from '../../../containers/Pages/ReportsPage/ChatReport/ChatReportFiltersContainer';
import {
    TicketFiltersMandatoryQueryKeys,
    TicketFiltersQueryKeyMap
} from '../../../containers/Pages/ReportsPage/TicketReport/TicketReportFiltersContainer';
import useCompanyTimeZone from '../../../hooks/adminPage/useCompanyTimeZone';
import { getTenantId } from '../../../utils/accountHelper';
import { guessTimeZone, nullishValue } from '../../../utils/helpers';
import ReportSelectionLayout from './ReportSelectionLayout';

export enum reportTypeMap {
    'tickets' = 'tickets',
    'agents' = 'agents',
    'call' = 'call',
    'chat' = 'chat',
    'external' = 'external',
    'agent_status' = 'agent_status',
    'agent_status_snapshot' = 'agent_status_snapshot'
}

/**
 * It takes a reportType and returns an array of strings that are the keys of the object that maps the
 * reportType to the query key map
 * @param {reportTypeMap} reportType - reportTypeMap
 * @returns An array of strings
 */
const filterQueryParams = (reportType: reportTypeMap) => {
    switch (reportType) {
        case reportTypeMap.tickets:
            return Object.keys(TicketFiltersQueryKeyMap);
        case reportTypeMap.agents:
            return Object.keys(AgentFiltersQueryKeyMap);
        case reportTypeMap.call:
            return Object.keys(CallFiltersQueryKeyMap);
        case reportTypeMap.chat:
            return Object.keys(ChatFiltersQueryKeyMap);
        case reportTypeMap.agent_status:
            return Object.keys(AgentStatusFiltersQueryKeyMap);
        case reportTypeMap.agent_status_snapshot:
            return Object.keys(AgentStatusSnapshotFiltersQueryKeyMap);
        default:
            return Object.keys(TicketFiltersQueryKeyMap);
    }
};
const mandatoryQueryParams = (reportType: reportTypeMap) => {
    switch (reportType) {
        case reportTypeMap.tickets:
            return Object.keys(TicketFiltersMandatoryQueryKeys);
        case reportTypeMap.agents:
            return Object.keys(AgentFiltersMandatoryQueryKeys);
        case reportTypeMap.call:
            return Object.keys(CallFiltersMandatoryQueryKeys);
        case reportTypeMap.chat:
            return Object.keys(ChatFiltersMandatoryQueryKeys);
        case reportTypeMap.agent_status:
            return Object.keys(AgentStatusFiltersMandatoryQueryKeys);
        case reportTypeMap.agent_status_snapshot:
            return Object.keys(AgentStatusSnapshotFiltersMandatoryQueryKeys);
        default:
            return Object.keys(TicketFiltersMandatoryQueryKeys);
    }
};

const getDefaultValues = (param: string, userTimeZone: string) => {
    switch (param) {
        case 'startDate':
            return dayjs().tz(userTimeZone).startOf('month').valueOf().toString();
        case 'endDate':
            return dayjs().tz(userTimeZone).endOf('month').valueOf().toString();
        case 'zone':
            return userTimeZone;
        case 'date':
            return dayjs().tz(userTimeZone).valueOf().toString();
        default:
            return userTimeZone;
    }
};

/**
 * It takes a query string and a report type, and returns a route string with the query string
 * @param {ParsedUrlQuery} query - ParsedUrlQuery - this is the query string object that Next.js gives
 * us.
 * @param {reportTypeMap} targetReport - The report type that you want to generate a route for.
 * @returns A string that is the route to the target report with the query string parameters that are
 * valid for the target report.
 */
export const reportRouteStringWithQueryString = (
    query: ParsedUrlQuery,
    targetReport: reportTypeMap,
    userTimeZone: string
) => {
    mandatoryQueryParams(targetReport).map((mandatoryParam) => {
        if (!Object.keys(query).find((key) => key === mandatoryParam)) {
            query = { [mandatoryParam]: getDefaultValues(mandatoryParam, userTimeZone), ...query };
        }
    });
    const generatedQueryString = Object.keys(query)
        .filter((param) => param !== 'reportType')
        .map((param) => {
            if (!filterQueryParams(targetReport).find((queryParam) => queryParam === param)) return null;
            return `${param}=${query[param].toString()}`;
        });
    return (
        `/reports/${targetReport}?` + generatedQueryString.filter((queryString) => !nullishValue(queryString)).join('&')
    );
};

const { Title } = Typography;

function getTranslationKey(reportType: reportTypeMap) {
    switch (reportType) {
        case reportTypeMap.agents:
            return `agentReports.${reportType}`;
        case reportTypeMap.tickets:
            return `ticketReports.${reportType}`;
        case reportTypeMap.chat:
            return `chatReports.${reportType}`;
        case reportTypeMap.external:
            return `externalReports.${reportType}`;
        case reportTypeMap.call:
            return reportType;
        case reportTypeMap.agent_status:
            return `agentStatusReports.${reportType}`;
        case reportTypeMap.agent_status_snapshot:
            return `agentStatusSnapshotReports.${reportType}`;
    }
}
export const checkMandatoryQueryParams = (query: ParsedUrlQuery, targetReport: reportTypeMap) => {
    let isRedirectNeeded = false;
    mandatoryQueryParams(targetReport).map((mandatoryParam) => {
        if (!Object.keys(query).find((key) => key === mandatoryParam)) {
            isRedirectNeeded = true;
        }
    });
    return isRedirectNeeded;
};
const ReportsLayout = (): JSX.Element => {
    const {
        token: { colorSplit, lineWidth, paddingMD, paddingSM }
    } = theme.useToken();

    const { t } = useTranslation('reports');
    const { query, push } = useRouter();
    const reportType = query?.reportType?.toString() ?? reportTypeMap.tickets;
    const { data: zone } = useCompanyTimeZone();
    const userTimeZone = zone ?? guessTimeZone();
    useEffect(() => {
        const isRedirectNeeded = checkMandatoryQueryParams(query, reportType as reportTypeMap);
        if (isRedirectNeeded) {
            push(reportRouteStringWithQueryString(query, reportType as reportTypeMap, userTimeZone));
        }
    }, [push, query, reportType, userTimeZone]);

    const onChange = (reportType: reportTypeMap) => {
        push(reportRouteStringWithQueryString(query, reportType, userTimeZone));
    };

    return (
        <Layout className="full-height">
            <Row
                style={{
                    paddingBottom: paddingSM,
                    paddingLeft: paddingSM,
                    paddingRight: paddingSM,
                    paddingTop: paddingMD,
                    borderBottom: `${lineWidth}px solid ${colorSplit}`
                }}
            >
                <Row style={{ height: '0px' }}>
                    <Title level={3} style={{ margin: 0 }}>
                        {t('reports')}
                    </Title>
                </Row>

                <Row justify="center" className="full-width">
                    <Segmented
                        size="large"
                        value={reportType}
                        onChange={onChange}
                        options={Object.keys(reportTypeMap)
                            .map((reportType: reportTypeMap) => ({
                                label: t(getTranslationKey(reportType)),
                                value: reportType
                            }))
                            .filter(
                                (segment) => getTenantId() === 'yolcu360' || segment.value !== reportTypeMap.external
                            )}
                    />
                </Row>
            </Row>
            <Layout.Content
                style={{
                    overflow: 'auto',
                    paddingBottom: paddingSM,
                    paddingLeft: paddingSM,
                    paddingRight: paddingSM,
                    paddingTop: 0
                }}
            >
                <ReportSelectionLayout type={reportType} />
            </Layout.Content>
        </Layout>
    );
};

export default ReportsLayout;
